import { clearRectangle, rectangleInside, type PresentationTarget, type Rectangle } from '../../../renderer/src/video-presentation';
import { chromeRegions, type ChromeDensity } from './platform-geometry';
import { socialChromeRegions } from '../../../renderer/src/social-geometry';
import { statisticTextOverflows } from './statistic-fit';

export interface CompositionElement { name: string; bounds: Rectangle; overflow: boolean }
const selectors: readonly [string, string][] = [
  ['Title', '.stage-heading h1'], ['Trace details', '.stage-heading p'], ['Branding', '.wordmark'],
  ['Route progress', '.route-state'], ['Elevation', '.elevation-overlay'],
  ['Playback progress', '.playback-render'], ['Map attribution', '.video-map-credits'],
];
function measurement(element: HTMLElement, name: string): CompositionElement | undefined {
  const rect = element.getBoundingClientRect();
  if (rect.width < 1 || rect.height < 1) return;
  return { name, bounds: { x: rect.x, y: rect.y, width: rect.width, height: rect.height },
    overflow: element.scrollWidth > element.clientWidth + 1 || element.scrollHeight > element.clientHeight + 1 };
}
/** Reads visible composition content only; platform controls live outside this document. */
export function compositionMeasurements(document: Document): CompositionElement[] {
  const elements: CompositionElement[] = [];
  const social = document.querySelector('.social-composition');
  if (social) {
    for (const element of social.querySelectorAll<HTMLElement>('[data-composition-element]')) {
      const value = measurement(element, element.dataset.compositionElement ?? 'Information');
      if (value) elements.push(element.matches('.social-stat') ? { ...value, overflow: statisticTextOverflows(element) } : value);
    }
    const canvas = document.querySelector<HTMLCanvasElement>('.maplibregl-canvas');
    if (canvas && canvas.dataset.riderX !== undefined && canvas.dataset.riderY !== undefined) {
      const rect = canvas.getBoundingClientRect();
      elements.push({ name: 'Trace marker', bounds: { x: rect.x + Number(canvas.dataset.riderX) - 8, y: rect.y + Number(canvas.dataset.riderY) - 8, width: 16, height: 16 }, overflow: false });
    }
    const map = document.querySelector<HTMLElement>('.map-card');
    if (map && (map.clientWidth !== document.documentElement.clientWidth || map.clientHeight !== document.documentElement.clientHeight)) {
      elements.push({ name: 'Full-frame map', bounds: map.getBoundingClientRect(), overflow: true });
    }
    return elements;
  }
  for (const [name, selector] of selectors) for (const element of document.querySelectorAll<HTMLElement>(selector)) {
    const value = measurement(element, name);
    if (value) elements.push(value);
  }
  for (const element of document.querySelectorAll<HTMLElement>('.metric')) {
    const name = `${element.querySelector('.metric-label')?.textContent ?? 'Trace'} statistics`;
    const value = measurement(element, name);
    if (value) elements.push({ ...value, overflow: statisticTextOverflows(element) });
  }
  const map = document.querySelector<HTMLElement>('.map-card');
  if (map && map.getBoundingClientRect().height < 96) elements.push({ name: 'Map area', bounds: map.getBoundingClientRect(), overflow: true });
  return elements;
}
function intersects(a: Rectangle, b: Rectangle): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}
export function previewWarnings(elements: readonly CompositionElement[], target: PresentationTarget,
  width: number, height: number, density: ChromeDensity, social = false): string[] {
  const clear = clearRectangle(target, width, height);
  const chrome = social ? socialChromeRegions(target, width, height, density) : chromeRegions(target, width, height, density);
  const rider = elements.find(element => element.name === 'Trace marker');
  const clearance = 48 * width / 1080;
  return [...new Set(elements.flatMap(element => {
    const messages: string[] = [];
    if (element.overflow) messages.push(`${element.name} overflows its available space.`);
    if (social && !rectangleInside(element.bounds, { x: 0, y: 0, width, height })) messages.push(`${element.name} extends outside the video frame.`);
    if (social && rider && element.name !== 'Trace marker' && intersects(element.bounds, {
      x: rider.bounds.x - clearance, y: rider.bounds.y - clearance,
      width: rider.bounds.width + clearance * 2, height: rider.bounds.height + clearance * 2,
    })) messages.push(`${element.name} overlaps the trace marker clear area.`);
    const overlaps = chrome.filter(region => intersects(element.bounds, region.bounds));
    if (overlaps.length) messages.push(`${element.name} overlaps ${overlaps.map(region => region.name.toLowerCase()).join(' and ')}.`);
    else if (target !== 'none' && !rectangleInside(element.bounds, clear)) messages.push(`${element.name} extends outside the clear-space guide.`);
    return messages;
  }))];
}
